import { captureException } from '@sentry/browser'
import { useQueryClient } from '@tanstack/react-query'
import { LobsterMasterApi } from '@ulysses-inc/harami_api_client'
import { useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { labelMasterQueryKeys } from 'src/features/labelMaster/api/queryKeys'
import { baseClient } from 'src/state/middleware/saga/baseClient'
import { useUploadLabelImage } from './useUploadLabelImage'

type UseMultipleLabelUploadProps = {
  labelMasterId: string
  maxCount: number
  count: number
  onSingleLabelAdded: (labelId: string) => void
}

type UploadResult = { success: true; labelId: string } | { success: false }

export const useMultipleLabelUpload = ({
  labelMasterId,
  maxCount,
  count,
  onSingleLabelAdded,
}: UseMultipleLabelUploadProps) => {
  const queryClient = useQueryClient()
  const [isUploading, setIsUploading] = useState(false)
  const { uploadLabelImage } = useUploadLabelImage()
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar()

  const uploadMultipleLabels = async (files: FileList) => {
    try {
      setIsUploading(true)

      const maxSelectableCount = maxCount - count
      if (files.length > maxSelectableCount) {
        showErrorSnackbar(
          `${maxCount}枚の画像を登録できます。現在${count}枚登録済みで、後${maxSelectableCount}枚まで登録可能です。`,
        )
        return
      }

      const fileArray = Array.from(files)
      const api = baseClient.getApi(LobsterMasterApi)

      const uploadPromises = fileArray.map(
        async (file, i): Promise<UploadResult> => {
          try {
            const path = crypto.randomUUID()
            const name = `ラベル${count + i + 1}`
            const result = await uploadLabelImage(file, path)
            if (!result) {
              return { success: false }
            }

            const response = await api.postInternalLabels({
              postInternalLabelRequestBody: {
                name,
                labelMasterId,
                labelImageId: result.id,
                saveAreas: [],
                deleteAreas: [],
                calibrationAreas: [],
              },
            })

            return { success: true, labelId: response.id }
          } catch (error) {
            captureException(error)
            return { success: false }
          }
        },
      )

      const results = await Promise.all(uploadPromises)
      const savedResults = results.filter(r => r.success)
      const failedResults = results.filter(r => !r.success)

      if (failedResults.length > 0) {
        showErrorSnackbar(
          `${failedResults.length}枚の画像のアップロードに失敗しました`,
        )
      }

      if (savedResults.length === 0) {
        showErrorSnackbar(
          'ラベルの追加に失敗しました。しばらく経ってから再度お試しください。',
        )
        return
      }

      await queryClient.invalidateQueries({
        queryKey: labelMasterQueryKeys.labels.byLabelMaster(labelMasterId),
      })

      if (savedResults.length === 1) {
        showSuccessSnackbar('ラベルを追加しました')
        const firstAddedLabelId = savedResults[0]!.labelId
        onSingleLabelAdded(firstAddedLabelId)
      } else if (savedResults.length > 1) {
        showSuccessSnackbar(`${savedResults.length}枚のラベルを追加しました`)
      }
    } finally {
      setIsUploading(false)
    }
  }

  return { uploadMultipleLabels, isUploading }
}
